<template>
  <div class="page-wrap">
    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <div class="panel-header__icon">
          <i class="fa fa-briefcase"></i>
        </div>
        <div class="panel-header__text">
          <div class="panel-header__title">{{ isNew ? 'Create Mobile Service' : 'Edit Mobile Service' }}</div>
          <div class="panel-header__sub" v-if="currentUser.u.is_service_provider && isNew">
            For {{ client_name }}
          </div>
          <div class="panel-header__sub" v-else>Mobile service job details</div>
        </div>
        <div class="panel-header__action">
          <button class="btn-back" @click="goBack()">
            <i class="fa fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      <!-- Job Details Section -->
      <div class="form-section">
        <div class="section-label">
          <i class="fa fa-cog"></i> Job Details
        </div>
        <div class="fields-grid">
          <div class="field-wrap">
            <label class="field-label">Job Title <span class="req">*</span></label>
            <input
              type="text"
              class="field-input"
              :class="{ 'field-input--error': errors['title'] }"
              v-model="jobForm.title"
              placeholder="Enter job title"
            />
            <span class="field-error" v-if="errors['title']">{{ errors['title'][0] }}</span>
          </div>

          <div class="field-wrap" v-if="!currentUser.u.is_client">
            <label class="field-label">Location <span class="req">*</span></label>
            <select class="field-select" v-model="client_id" @change="fetchOperationnByClient">
              <option value="all">Select Location</option>
              <option
                v-for="(citem, index) in getClients"
                :key="index"
                v-show="citem.is_mobile_fingerprinting"
                :value="citem.id"
              >{{ citem.name }}</option>
            </select>
          </div>

          <div class="field-wrap">
            <label class="field-label">Service</label>
            <select class="field-select" v-model="jobForm.service_id">
              <option value="">Select Service</option>
              <option
                v-for="(service, index) in getBRateLists.rates"
                :key="index"
                :value="service.service.id"
              >{{ service.service.name }}</option>
            </select>
          </div>

          <div class="field-wrap">
            <label class="field-label">Operator</label>
            <select class="field-select" v-model="jobForm.operator_id">
              <option value="">Select Operator</option>
              <option v-for="(user, index) in users" :key="index" :value="user.id">
                {{ user.username }}
              </option>
            </select>
          </div>

          <div class="field-wrap">
            <label class="field-label">Payment Method</label>
            <select class="field-select" v-model="jobForm.payment_type">
              <option value="">None</option>
              <option value="Certified Check">Certified Check</option>
              <option value="Money Order">Money Order</option>
              <option value="Credit Card Form">Credit Card Form</option>
              <option value="Cash">Cash</option>
            </select>
          </div>

          <div class="field-wrap">
            <label class="field-label">Scheduled Date</label>
            <input type="date" class="field-input" v-model="jobForm.scheduledDate" />
          </div>

          <div class="field-wrap">
            <label class="field-label">Scheduled Time</label>
            <input type="time" class="field-input" v-model="jobForm.scheduledTime" />
          </div>

          <div class="field-wrap">
            <label class="field-label">Status</label>
            <select class="field-select" v-model="jobForm.status">
              <option value="Open">Open</option>
              <option value="Closed">Closed</option>
            </select>
          </div>

          <div class="field-wrap">
            <label class="field-label">Payment By</label>
            <select class="field-select" v-model="jobForm.payment_by">
              <option value="Individual">Individual</option>
              <option value="Company">Company</option>
            </select>
          </div>
        </div>
      </div>

      <div class="section-divider"></div>

      <!-- Two-column sections -->
      <div class="two-col-wrap">

        <!-- Client Information -->
        <div class="info-section">
          <div class="section-label">
            <i class="fa fa-user"></i> Client Information
          </div>

          <div class="fields-grid fields-grid--2">
            <div class="field-wrap">
              <label class="field-label">C/O</label>
              <input type="text" class="field-input" v-model="jobForm.care_of" placeholder="Care of" />
            </div>
            <div class="field-wrap">
              <label class="field-label">ATTN</label>
              <input type="text" class="field-input" v-model="jobForm.attn" placeholder="Attention" />
            </div>
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Name</label>
            <input type="text" class="field-input" v-model="jobForm.client.name" @input="UpdateContact" />
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Phone Number</label>
            <input
              type="text"
              class="field-input"
              v-mask="'(###)-###-####'"
              v-model="jobForm.client.phone_number"
              @input="UpdateContact"
            />
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Email</label>
            <input
              type="email"
              class="field-input"
              :class="{ 'field-input--error': errors['client_email'] }"
              v-model="jobForm.client.email"
              @input="UpdateContact"
            />
            <span class="field-error" v-if="errors['client_email']">{{ errors['client_email'][0] }}</span>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">Address Line 1</label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['client_address'] }"
                v-model="jobForm.client.address"
                @input="UpdateContact"
              />
              <span class="field-error" v-if="errors['client_address']">{{ errors['client_address'][0] }}</span>
            </div>
            <div class="field-wrap">
              <label class="field-label">Address Line 2</label>
              <input type="text" class="field-input" v-model="jobForm.client.address1" @input="UpdateContact" />
            </div>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">City</label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['client_city'] }"
                v-model="jobForm.client.city"
                @input="UpdateContact"
              />
              <span class="field-error" v-if="errors['client_city']">{{ errors['client_city'][0] }}</span>
            </div>
            <div class="field-wrap">
              <label class="field-label">State</label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['client_state'] }"
                v-model="jobForm.client.state"
                @input="UpdateContact"
              />
              <span class="field-error" v-if="errors['client_state']">{{ errors['client_state'][0] }}</span>
            </div>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">Zip Code</label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['client_zipcode'] }"
                v-model="jobForm.client.zipcode"
                @input="UpdateContact"
              />
              <span class="field-error" v-if="errors['client_zipcode']">{{ errors['client_zipcode'][0] }}</span>
            </div>
            <div class="field-wrap">
              <label class="field-label">Country</label>
              <input
                type="text"
                class="field-input"
                :class="{ 'field-input--error': errors['client_country'] }"
                v-model="jobForm.client.country"
                @input="UpdateContact"
              />
              <span class="field-error" v-if="errors['client_country']">{{ errors['client_country'][0] }}</span>
            </div>
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Address (Map)</label>
            <keep-alive>
              <GoogleMap
                ref="clientmap"
                @getFullAddress="setFullAddress"
                @dragPointer="setLocation"
                @onSearchClick="setLocation"
              ></GoogleMap>
            </keep-alive>
          </div>
        </div>

        <!-- Contact Person Information -->
        <div class="info-section">
          <div class="section-label">
            <i class="fa fa-address-card"></i> Contact Person Information
          </div>

          <div class="checkbox-row">
            <label class="check-wrap">
              <input
                type="checkbox"
                class="check-input"
                v-model="useClientDetail"
                @change="updateContactDetail"
              />
              <span class="check-label">Use Client Detail</span>
            </label>
            <label class="check-wrap">
              <input
                type="checkbox"
                class="check-input"
                v-model="jobForm.use_in_form"
              />
              <span class="check-label">Use in 1-783 form</span>
            </label>
          </div>

          <div class="field-wrap mt-16">
            <label class="field-label">Name</label>
            <input type="text" class="field-input" v-model="jobForm.person.name" />
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Phone Number</label>
            <input
              type="text"
              class="field-input"
              v-mask="'(###)-###-####'"
              v-model="jobForm.person.contact_number"
            />
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Email</label>
            <input
              type="email"
              class="field-input"
              :class="{ 'field-input--error': errors['contact_email'] }"
              v-model="jobForm.person.email"
            />
            <span class="field-error" v-if="errors['contact_email']">{{ errors['contact_email'][0] }}</span>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">Address Line 1</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.address" @input="UpdateContact" />
            </div>
            <div class="field-wrap">
              <label class="field-label">Address Line 2</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.address1" @input="UpdateContact" />
            </div>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">City</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.city" @input="UpdateContact" />
            </div>
            <div class="field-wrap">
              <label class="field-label">State</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.state" @input="UpdateContact" />
            </div>
          </div>

          <div class="fields-grid fields-grid--2 mt-12">
            <div class="field-wrap">
              <label class="field-label">Zip Code</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.zipcode" @input="UpdateContact" />
            </div>
            <div class="field-wrap">
              <label class="field-label">Country</label>
              <input type="text" class="field-input" v-model="jobForm.serviceLocation.country" @input="UpdateContact" />
            </div>
          </div>

          <div class="field-wrap mt-12">
            <label class="field-label">Service Location (Map)</label>
            <keep-alive>
              <GoogleMap
                ref="servicemap"
                @getFullAddress="setServiceLocationAddress"
                @dragPointer="setServiceLocation"
                @onSearchClick="setServiceLocation"
              ></GoogleMap>
            </keep-alive>
          </div>
        </div>
      </div>

      <!-- bottom padding for save bar -->
      <div style="height: 80px;"></div>
    </div>

    <!-- Floating save bar -->
    <div class="save-bar">
      <div class="save-bar__left"></div>
      <div class="save-bar__right">
        <button class="save-bar__back" @click="goBack()">
          <i class="fa fa-arrow-left"></i> Back
        </button>
        <button
          v-if="isNew"
          class="save-bar__submit"
          ref="store_job_detail"
          @click="store('store_job_detail')"
        >
          <i class="fa fa-check"></i> Save
        </button>
        <button
          v-if="!isNew"
          class="save-bar__submit"
          ref="kt_update_job"
          @click="store('kt_update_job')"
        >
          <i class="fa fa-check"></i> Update
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import {
  FETCH_B_LOCATION_RATE_LISTS,
  SAVE_CLIENT_JOB_DETAILS,
  FETCH_CLIENT_JOB_DETAILS,
  FETCH_B_USER,
  FETCH_CLIENTS,
  FETCH_B_LOCATION_AND_SERVICE_PROVIDER_USER
} from "@/core/services/store/actions.type";
import GoogleMap from "@/components/GoogleMap.vue";
export default {
  components: {
    GoogleMap
  },
  computed: {
    ...mapGetters(["getClients","getBRateLists", "currentUser", "getJobDetails"])
  },
  data() {
    return {
      client_id:'all',
      errors: [],
      useClientDetail: false,
      users: [],
      client_name: "",
      jobForm: {
        care_of: "",
        attn: "",
        payment_type: "",
        use_in_form: false,
        serviceLocation: {
          lat: "",
          lng: "",
          address: "",
          address1: "",
          city: "",
          country: "",
          state: "",
          zipcode: ""
        },
        title: "",
        status: "Closed",
        client: {
          name: "",
          phone_number: "",
          email: "",
          address: "",
          address1: "",
          lat: "",
          lng: "",
          city: "",
          country: "",
          state: "",
          zipcode: ""
        },
        person: {
          name: "",
          contact_number: "",
          email: ""
        },
        payment_by: "Individual",
        scheduledDate: "",
        scheduledTime: "",
        service_id: "",
        operator_id: ""
      },

      job_id: 0,
      client_slug: "",
      isNew: true,
      breadCrumbs: [
        {
          id: 1,
          title: "Dashboard",
          route: "client.dashboard"
        },
        {
          id: 2,
          title: "Mobile Service",
          route: "client.job.list"
        },
        {
          id: 3,
          title: "Create New",
          route: ""
        }
      ]
    };
  },
  created() {
    this.$store.dispatch(FETCH_CLIENTS);
    this.client_slug = this.$route.params.client_slug;
    this.job_id = this.$route.params.job_id;
    var data = {
      client_slug: this.$route.params.client_slug,
      client_id: this.$route.params.client_id
    };
    if (this.currentUser.u.is_service_provider) {
      this.$store
        .dispatch(FETCH_B_LOCATION_AND_SERVICE_PROVIDER_USER, data)
        .then(data => {
          this.users = data.users;
          this.client_name = data.client.name;
        });
    } else {
      var _data = {
        is_client: this.currentUser.u.is_client,
        is_service_provider: this.currentUser.u.is_service_provider
      };
      this.$store.dispatch(FETCH_B_USER, _data).then(data => {
        this.users = data;
      });
    }

    if (this.job_id != 0) {
      this.fetchJobDetails();
    }
    if (this.job_id != 0) {
      this.breadCrumbs[2].title = "Edit";
    }
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);

    this.$store.dispatch(
      FETCH_B_LOCATION_RATE_LISTS,
      this.$route.params.client_id,
    );
  },

  methods: {
    store(ref) {
      if (this.client_id == "all" && !this.currentUser.u.is_client) {
        this.$toastr.e("Please select a location to create new job");
      } else {
        var client_id = this.client_id;
        var data = {
          title: this.jobForm.title,
          status: this.jobForm.status,
          use_in_form: this.jobForm.use_in_form,
          payment_type: this.jobForm.payment_type,
          service: this.jobForm.service_id,
          client_name: this.jobForm.client.name,
          client_address: this.jobForm.client.address,
          client_phone_no: this.jobForm.client.phone_number,
          client_email: this.jobForm.client.email,
          contact_person: this.jobForm.person.name,
          contact_email: this.jobForm.person.email,
          contact_phone_no: this.jobForm.person.contact_number,
          client_long: this.jobForm.client.lng,
          client_state: this.jobForm.client.state,
          client_city: this.jobForm.client.city,
          client_zipcode: this.jobForm.client.zipcode,
          client_country: this.jobForm.client.country,
          client_address1: this.jobForm.client.address1,
          client_lat: this.jobForm.client.lat,
          schedule_date: this.jobForm.scheduledDate,
          schedule_time: this.jobForm.scheduledTime,
          payment_by: this.jobForm.payment_by,
          city: this.jobForm.serviceLocation.city,
          state: this.jobForm.serviceLocation.state,
          country: this.jobForm.serviceLocation.country,
          zipcode: this.jobForm.serviceLocation.zipcode,
          care_of: this.jobForm.care_of,
          attn: this.jobForm.attn,
          service_location_address: this.jobForm.serviceLocation.address,
          service_location_address1: this.jobForm.serviceLocation.address1,
          service_location_lat: this.jobForm.serviceLocation.lat,
          service_location_lng: this.jobForm.serviceLocation.lng,
          id: parseInt(this.job_id),
          client_id: client_id,
          operator: parseInt(this.jobForm.operator_id)
        };

        this.openLoader(ref);

        this.$store
          .dispatch(SAVE_CLIENT_JOB_DETAILS, data)
          .then(data => {
            this.$toastr.s(data.msg);
            if (ref == "kt_update_job") {
              this.$router.push({
                name: "client.job.details",
                params: { client_slug: this.client_slug, job_id: this.job_id }
              });
              this.closeBtnLoad(ref);
            } else {
              this.$router.push({ name: "client.job.list" });
              this.closeBtnLoad(ref);
            }
          })
          .catch(error => {
            this.errors = error;
            this.closeBtnLoad(ref);
          });
      }
    },
    openLoader(ref) {
      const submitButton = this.$refs[ref];
      submitButton.disabled = true;
      submitButton.classList.add("spinner", "spinner-light", "spinner-right");
    },
    closeBtnLoad(ref) {
      const submitButton = this.$refs[ref];
      submitButton.classList.remove("spinner", "spinner-light", "spinner-right");
      submitButton.disabled = false;
    },
    goBack() {
      this.$router.push({ name: "client.job.list" });
    },
    setLocation(location) {
      this.jobForm.client.lng = location.lng;
      this.jobForm.client.lat = location.lat;
      this.jobForm.client.city = location.city.long_name;
      this.jobForm.client.address = location.formatted_address;
      this.jobForm.client.zipcode = location.postal_code.long_name;
      this.jobForm.client.country = location.country.long_name;
      this.jobForm.client.state = location.state.long_name;
    },
    setFullAddress(data) {
      this.jobForm.client.address = data;
    },
    setServiceLocation(location) {
      this.jobForm.serviceLocation.lng = location.lng;
      this.jobForm.serviceLocation.lat = location.lat;
      this.jobForm.serviceLocation.city = location.city.long_name;
      this.jobForm.serviceLocation.address = location.formatted_address;
      this.jobForm.serviceLocation.zipcode = location.postal_code.long_name;
      this.jobForm.serviceLocation.country = location.country.long_name;
      this.jobForm.serviceLocation.state = location.state.long_name;
    },
    setServiceLocationAddress(data) {
      this.jobForm.serviceLocation.address = data;
    },
    updateContactDetail(e) {
      if (e.target.checked) {
        this.jobForm.person.name = this.jobForm.client.name;
        this.jobForm.person.contact_number = this.jobForm.client.phone_number;
        this.jobForm.person.email = this.jobForm.client.email;
        this.jobForm.serviceLocation.lat = this.jobForm.client.lat;
        this.jobForm.serviceLocation.lng = this.jobForm.client.lng;
        this.jobForm.serviceLocation.address = this.jobForm.client.address;
        this.jobForm.serviceLocation.address1 = this.jobForm.client.address1;
        this.jobForm.serviceLocation.city = this.jobForm.client.city;
        this.jobForm.serviceLocation.zipcode = this.jobForm.client.zipcode;
        this.jobForm.serviceLocation.country = this.jobForm.client.country;
        this.jobForm.serviceLocation.state = this.jobForm.client.state;
        this.$refs.servicemap.defaultMarkerPosition(
          Number(this.jobForm.serviceLocation.lat),
          Number(this.jobForm.serviceLocation.lng)
        );
      }
    },
    UpdateContact() {
      if (this.useClientDetail) {
        this.jobForm.person.name = this.jobForm.client.name;
        this.jobForm.person.contact_number = this.jobForm.client.phone_number;
        this.jobForm.person.email = this.jobForm.client.email;
        this.jobForm.serviceLocation.address = this.jobForm.client.address;
        this.jobForm.serviceLocation.address1 = this.jobForm.client.address1;
        this.jobForm.serviceLocation.lat = this.jobForm.client.lat;
        this.jobForm.serviceLocation.lng = this.jobForm.client.lng;
        this.jobForm.serviceLocation.city = this.jobForm.client.city;
        this.jobForm.serviceLocation.zipcode = this.jobForm.client.zipcode;
        this.jobForm.serviceLocation.country = this.jobForm.client.country;
        this.jobForm.serviceLocation.state = this.jobForm.client.state;
        this.$refs.servicemap.defaultMarkerPosition(
          Number(this.jobForm.serviceLocation.lat),
          Number(this.jobForm.serviceLocation.lng)
        );
      }
    },
    fetchJobDetails() {
      this.isNew = false;
      this.$store
        .dispatch(FETCH_CLIENT_JOB_DETAILS, {
          id: parseInt(this.job_id),
          list: false
        })
        .then(async data => {
          this.client_id = data.data.client;
          await this.fetchOperationnByClient();

          var data_ = data.data;
          this.jobForm.title = data_.title;
          this.jobForm.status = data_.status;
          this.jobForm.service_id = data_.service;
          this.jobForm.use_in_form = data_.use_in_form;
          this.jobForm.care_of = data_.care_of;
          this.jobForm.attn = data_.attn;
          this.jobForm.payment_type = data_.payment_type;
          this.jobForm.scheduledDate = data_.schedule_date;
          this.jobForm.scheduledTime = data_.schedule_time;
          this.jobForm.person.name = data_.contact_person;
          this.jobForm.person.email = data_.contact_email;
          this.jobForm.person.contact_number = data_.contact_phone_no;
          this.jobForm.client.name = data_.client_name;
          this.jobForm.client.email = data_.client_email;
          this.jobForm.client.phone_number = data_.client_phone_no;
          this.jobForm.client.address = data_.client_address;
          this.jobForm.client.address1 = data_.client_address1;
          this.jobForm.client.zipcode = data_.client_zipcode;
          this.jobForm.client.country = data_.client_country;
          this.jobForm.client.state = data_.client_state;
          this.jobForm.client.city = data_.client_city;
          this.jobForm.client.lat = data_.client_lat;
          this.jobForm.client.lng = data_.client_long;
          this.jobForm.payment_by = data_.payment_by;
          this.jobForm.serviceLocation.lat = data_.service_location_lat;
          this.jobForm.serviceLocation.lng = data_.service_location_lng;
          this.jobForm.serviceLocation.city = data_.city;
          this.jobForm.serviceLocation.address = data_.service_location_address;
          this.jobForm.serviceLocation.address1 = data_.service_location_address1;
          this.jobForm.serviceLocation.zipcode = data_.zipcode;
          this.jobForm.serviceLocation.country = data_.country;
          this.jobForm.serviceLocation.state = data_.state;
          this.jobForm.operator_id = data_.operator;
          this.jobForm.serviceLocation.location = data_.service_location_address;
          this.$refs.clientmap.defaultMarkerPosition(
            Number(data_.client_lat),
            Number(data_.client_long)
          );
          this.$refs.servicemap.defaultMarkerPosition(
            Number(data_.service_location_lat),
            Number(data_.service_location_lng)
          );
        });
    },
    async fetchOperationnByClient() {
      await this.$store.dispatch(FETCH_B_LOCATION_RATE_LISTS, this.client_id);
      this.client_slug = this.$route.params.client_slug;
      this.job_id = this.$route.params.job_id;
      var data = {
        client_slug: this.$route.params.client_slug,
        client_id: this.client_id
      };
      if (this.currentUser.u.is_service_provider) {
        await this.$store
          .dispatch(FETCH_B_LOCATION_AND_SERVICE_PROVIDER_USER, data)
          .then(data => {
            this.users = data.users;
            this.client_name = data.client.name;
          });
      } else {
        var _data = {
          is_client: this.currentUser.u.is_client,
          is_service_provider: this.currentUser.u.is_service_provider
        };
        await this.$store.dispatch(FETCH_B_USER, _data).then(data => {
          this.users = data;
        });
      }
    }
  }
};
</script>

<style lang="scss" scoped>
$font:   'Segoe UI', sans-serif;
$blue:   #389ad4;
$dark:   #0d1b3e;
$green:  #276749;
$border: #dde2ec;

/* ── page ── */
.page-wrap {
  padding: 12px;
  font-family: $font;
  padding-bottom: 100px;
}

/* ── panel card ── */
.panel-card {
  background: #fff;
  border: 1px solid $border;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0,0,0,0.05);
}

/* ── panel header ── */
.panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, #f8faff 0%, #f0f5ff 100%);

  &__icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, $blue, #2277aa);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 18px;
    flex-shrink: 0;
  }

  &__text { flex: 1; }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $dark;
    line-height: 1.2;
  }

  &__sub {
    font-size: 12px;
    color: #7a8499;
    margin-top: 3px;
  }

  &__action { flex-shrink: 0; }
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f0f3fa;
  color: $dark;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  font-family: $font;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #e4eaf5;
    border-color: #b8c5dc;
  }
}

/* ── form section ── */
.form-section {
  padding: 24px 24px 20px;
}

.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $blue;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-divider {
  height: 1px;
  background: $border;
  margin: 0 24px;
}

/* ── fields grid ── */
.fields-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 14px 16px;

  &--2 {
    grid-template-columns: 1fr 1fr;
  }
}

.mt-12 { margin-top: 14px; }
.mt-16 { margin-top: 16px; }

/* ── field ── */
.field-wrap {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.field-label {
  font-size: 12px;
  font-weight: 500;
  color: #5a6480;
}

.req { color: #e53935; }

.field-input,
.field-select {
  height: 38px;
  border: 1.5px solid $border;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  font-family: $font;
  color: $dark;
  background: #fafbfd;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  width: 100%;

  &::placeholder { color: #b0bac9; }

  &:focus {
    border-color: $blue;
    box-shadow: 0 0 0 3px rgba(56,154,212,0.12);
    background: #fff;
  }

  &--error {
    border-color: #e53935;
    &:focus { box-shadow: 0 0 0 3px rgba(229,57,53,0.1); }
  }
}

.field-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23a0aab8' d='M1 1l5 5 5-5'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-color: #fafbfd;
  padding-right: 28px;
  cursor: pointer;
}

.field-error {
  font-size: 11px;
  color: #e53935;
}

/* ── two-col layout ── */
.two-col-wrap {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  border-top: 1px solid $border;
}

.info-section {
  padding: 24px;

  &:first-child {
    border-right: 1px solid $border;
  }
}

/* ── checkboxes ── */
.checkbox-row {
  display: flex;
  gap: 24px;
  align-items: center;
  padding: 12px 0 4px;
}

.check-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.check-input {
  width: 16px;
  height: 16px;
  accent-color: $blue;
  cursor: pointer;
}

.check-label {
  font-size: 13px;
  color: $dark;
  user-select: none;
}

/* ── floating save bar ── */
.save-bar {
  position: fixed;
  bottom: 2.5rem;
  width: calc(78vw);
  left: 50%;
  transform: translateX(-40%);
  background: #1a2340;
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  z-index: 200;

  &__left { flex: 1; }

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.1);
    color: #fff;
    border: 1.5px solid rgba(255,255,255,0.2);
    border-radius: 8px;
    padding: 8px 18px;
    font-size: 13px;
    font-weight: 500;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: rgba(255,255,255,0.18); }
  }

  &__submit {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: $blue;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 8px 22px;
    font-size: 13px;
    font-weight: 600;
    font-family: $font;
    cursor: pointer;
    transition: background 0.15s, box-shadow 0.15s;

    &:hover {
      background: #2d85bb;
      box-shadow: 0 4px 14px rgba(56,154,212,0.4);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }
}

/* ── responsive ── */
@media (max-width: 768px) {
  .two-col-wrap {
    grid-template-columns: 1fr;
  }
  .info-section:first-child {
    border-right: none;
    border-bottom: 1px solid $border;
  }
  .fields-grid--2 {
    grid-template-columns: 1fr;
  }
  .save-bar {
    width: calc(100vw - 32px);
    left: 16px;
    transform: none;
    bottom: 1rem;
  }
}
</style>
